//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';

import { PrimaryLinkButton } from '@components/atoms/button/Button';
import Spinner from '@components/atoms/spinner/Spinner';
import { usePopover } from '@components/molecules/popover/usePopover';
import { useSmartInputContext } from '@components/molecules/smart-input/SmartInputContext';
import { LinkWrapper } from '@components/molecules/smart-input/link-shortening/LinkWrapper';
import { insertLinkAtPosition } from '@components/molecules/smart-input/link-shortening/linkShorteningUtils';
import { IDraftDecoratorComponentProps } from '@components/molecules/smart-input/smartInputModel';
import { SmartInputTrackingId } from '@components/molecules/smart-input/smartInputTrackingIds';
import { getSmartInputTrackingId } from '@components/molecules/smart-input/smartInputUtils';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import {
  getUrlWithSecureProtocol,
  getUrlWithoutProtocol,
} from '@utils/urlUtils';
import { hasValidProtocol } from '@utils/urlValidation';

interface Props extends IDraftDecoratorComponentProps {
  children: React.ReactNode;
}

export const ShortLink = ({
  blockKey,
  children,
  decoratedText,
  end,
  start,
}: Props) => {
  const smartInputContext = useSmartInputContext();
  const [isLoadingOriginalLink, setIsLoadingOriginalLink] = useState(false);
  const [originalLinkValue, setOriginalLinkValue] = useState<string>('');
  const [hasErrorGettingOriginalLink, setHasErrorGettingOriginalLink] =
    useState(false);
  const textRef = useRef<HTMLSpanElement>(null);

  const [connectPopover, { closePopover }] = usePopover();

  const debouncedGetOriginalLink = useDebouncedCallback(
    async (shortLink: string) => {
      if (shortLink === '') {
        return;
      }

      setIsLoadingOriginalLink(true);
      setHasErrorGettingOriginalLink(false);
      setOriginalLinkValue('');

      const originalLink =
        await smartInputContext.linkShortening?.getOriginalLink(
          hasValidProtocol(shortLink)
            ? shortLink
            : getUrlWithSecureProtocol(shortLink),
        );

      if (originalLink) {
        const urlWithoutProtocol = hasValidProtocol(originalLink)
          ? getUrlWithoutProtocol(originalLink)
          : originalLink;

        setOriginalLinkValue(urlWithoutProtocol);
      } else {
        setHasErrorGettingOriginalLink(true);
      }

      setIsLoadingOriginalLink(false);
    },
    500,
  );

  // Handle getting the original link from the short link when it mounts/changes
  useEffect(() => {
    void debouncedGetOriginalLink(decoratedText);
  }, [debouncedGetOriginalLink, decoratedText]);

  const handleShortenLink = (shortLink: string) => {
    return insertLinkAtPosition(shortLink, smartInputContext, {
      blockKey,
      start,
      end,
    });
  };

  return (
    <SmallTooltip
      appendTarget={'body'}
      connectPopover={connectPopover}
      content={
        <div>
          {isLoadingOriginalLink ? (
            <Spinner />
          ) : (
            <>
              <LinkText
                {...(!hasErrorGettingOriginalLink && {
                  title: originalLinkValue,
                })}
              >
                {hasErrorGettingOriginalLink
                  ? 'Unable to show original URL'
                  : originalLinkValue}
              </LinkText>
              <PrimaryLinkButton
                onClick={() => {
                  // Close popover before opening modal so they do not overlap
                  closePopover();
                  if (originalLinkValue && !hasErrorGettingOriginalLink) {
                    smartInputContext.linkShortening?.openModal({
                      initialOriginalLinkValue: originalLinkValue,
                      initialShortLinkValue: decoratedText,
                      insertShortLink: handleShortenLink,
                      isEditingLink: true,
                    });
                  } else {
                    smartInputContext.linkShortening?.openModal({
                      insertShortLink: handleShortenLink,
                      isEditingLink: false,
                    });
                  }
                }}
                trackingId={getSmartInputTrackingId(
                  smartInputContext.trackingIdPrefix,
                  SmartInputTrackingId.ShortLinkOpenModalInlineTooltipEditButton,
                )}
              >
                {originalLinkValue ? 'Edit Original' : 'Add New Short Link'}
              </PrimaryLinkButton>
            </>
          )}
        </div>
      }
      isDisabled={false}
      target={
        <LinkWrapper ref={textRef} hasError={hasErrorGettingOriginalLink}>
          {children}
        </LinkWrapper>
      }
    />
  );
};

const LinkText = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
